import { useEffect, useRef } from 'react';
import type { CallEvent, ConnectionHealth } from '../types';

const RETRY_BASE_DELAY = 2000;
const RETRY_MAX_DELAY = 20000;

interface UseCallStreamOptions {
  onStatusChange?: (status: ConnectionHealth) => void;
}

export default function useCallStream(
  handler: (event: CallEvent) => void,
  streamUrl: string | null,
  options?: UseCallStreamOptions,
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const statusHandlerRef = useRef(options?.onStatusChange);
  statusHandlerRef.current = options?.onStatusChange;

  const notifyStatus = (status: ConnectionHealth) => {
    statusHandlerRef.current?.(status);
  };

  useEffect(() => {
    if (!streamUrl) {
      notifyStatus('disconnected');
      return undefined;
    }
    let isActive = true;
    let attempt = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let currentEventSource: EventSource | null = null;

    const connect = () => {
      if (!isActive) return;
      notifyStatus('connecting');
      const eventSource = new EventSource(streamUrl, { withCredentials: false });
      currentEventSource = eventSource;

      eventSource.onopen = () => {
        attempt = 0;
        notifyStatus('connected');
      };

      eventSource.addEventListener('existing_call', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data);
          handlerRef.current({ type: 'existing_call', data });
        } catch (error) {
          console.error('Invalid existing_call payload', error);
        }
      });

      eventSource.addEventListener('new_call', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data);
          handlerRef.current({ type: 'new_call', data });
        } catch (error) {
          console.error('Invalid new_call payload', error);
        }
      });

      eventSource.addEventListener('ended', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data);
          handlerRef.current({ type: 'ended', data });
        } catch (error) {
          console.error('Invalid ended payload', error);
        }
      });

      eventSource.onerror = () => {
        notifyStatus('disconnected');
        eventSource.close();
        if (!isActive) return;
        const jitter = Math.random() * 1000;
        const backoff = Math.min(RETRY_MAX_DELAY, RETRY_BASE_DELAY * 2 ** attempt);
        const delay = backoff + jitter;
        timeout = setTimeout(() => {
          if (!isActive) {
            return;
          }
          connect();
        }, delay);
        attempt += 1;
      };

      return eventSource;
    };

    connect();

    return () => {
      isActive = false;
      if (timeout) {
        clearTimeout(timeout);
      }
      currentEventSource?.close();
      currentEventSource = null;
      notifyStatus('disconnected');
    };
  }, [streamUrl]);
}
